import React, { useEffect, useRef, useState } from "react";
import { FaArrowLeft, FaArrowRight, FaPlay } from "react-icons/fa";

const ClientWork = () => {
  const clientVideos = [
    {
      id: "DyG6NhmQyZQ",
      thumbnail: `https://img.youtube.com/vi/DyG6NhmQyZQ/maxresdefault.jpg`,
    },
    {
      id: "b6vKvgyjdhw",
      thumbnail: `https://img.youtube.com/vi/b6vKvgyjdhw/maxresdefault.jpg`,
    },
    {
      id: "n7eM68UQcgY",
      thumbnail: `https://img.youtube.com/vi/n7eM68UQcgY/maxresdefault.jpg`,
    },
    {
      id: "dwQsC_lfLig",
      thumbnail: `https://img.youtube.com/vi/dwQsC_lfLig/hqdefault.jpg`,
    },
    {
      id: "f08HGR09TKk",
      thumbnail: `https://img.youtube.com/vi/f08HGR09TKk/maxresdefault.jpg`,
    },
    {
      id: "wwdCPWdLS2E",
      thumbnail: `https://img.youtube.com/vi/wwdCPWdLS2E/maxresdefault.jpg`,
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const autoPlayRef = useRef(null);
  const sliderRef = useRef(null);

  const totalSlides = clientVideos.length;

  // Handle slide transition
  const handleTransitionEnd = () => {
    if (currentIndex <= 0) {
      setIsTransitioning(false);
      setCurrentIndex(totalSlides);
    } else if (currentIndex >= totalSlides + 1) {
      setIsTransitioning(false);
      setCurrentIndex(1);
    }
  };

  // Navigation functions
  const goToSlide = React.useCallback((index) => {
    setIsTransitioning(true);
    setCurrentIndex(index);
  }, []);

  const nextSlide = React.useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev >= totalSlides + 1 ? 1 : prev + 1));
  }, [totalSlides]);

  const prevSlide = React.useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev <= 0 ? totalSlides : prev - 1));
  }, [totalSlides]);

  // Video player controls
  const playVideo = (videoId) => {
    setIsPaused(true);
    setIsPlaying(true);
    setActiveVideoId(videoId);
  };

  const closeVideo = () => {
    setIsPlaying(false);
    setActiveVideoId(null);
    setIsPaused(false);
  };

  // Auto-play effect
  useEffect(() => {
    if (!isPaused) {
      autoPlayRef.current = setTimeout(() => {
        nextSlide();
      }, 5000);
    }

    return () => {
      if (autoPlayRef.current) {
        clearTimeout(autoPlayRef.current);
      }
    };
  }, [currentIndex, isPaused, nextSlide]);

  // Calculate actual index for display purposes
  const getActualIndex = () => {
    if (currentIndex === 0) return totalSlides - 1;
    if (currentIndex === totalSlides + 1) return 0;
    return currentIndex - 1;
  };

  // Swipe handlers (simplified without external library)
  const handleTouchStart = useRef(null);

  const handleSwipe = React.useCallback((e) => {
    if (handleTouchStart.current === null) {
      handleTouchStart.current = e.touches[0].clientX;
    }
  }, []);

  const handleSwipeEnd = React.useCallback(
    (e) => {
      if (handleTouchStart.current === null) return;

      const touchEnd = e.changedTouches[0].clientX;
      const diff = handleTouchStart.current - touchEnd;

      if (diff > 50) {
        nextSlide();
      } else if (diff < -50) {
        prevSlide();
      }

      handleTouchStart.current = null;
    },
    [nextSlide, prevSlide]
  );

  return (
    <section
      id="clientwork"
      className="flex flex-col justify-center items-center bg-[#090522] p-8 overflow-x-hidden"
    >
      <h2 className="text-3xl font-bold mb-4">
        <span className="text-orange-500">CLIENT</span>{" "}
        <span className="text-yellow-400">Work</span>
      </h2>
      <p className="text-lg text-gray-400 max-w-3xl text-center mb-8">
        Showcasing the exceptional work created for our clients.
      </p>

      <div className="max-w-5xl w-full relative">
        {!isPlaying && (
          <>
            <button
              onClick={prevSlide}
              className="absolute top-1/2 -left-4 transform -translate-y-1/2 bg-gray-800 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-600 z-10"
            >
              <FaArrowLeft />
            </button>
            <button
              onClick={nextSlide}
              className="absolute top-1/2 -right-4 transform -translate-y-1/2 bg-gray-800 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-600 z-10"
            >
              <FaArrowRight />
            </button>
          </>
        )}

        <div
          className="overflow-hidden rounded-lg relative"
          onTouchStart={handleSwipe}
          onTouchEnd={handleSwipeEnd}
        >
          {isPlaying && activeVideoId && (
            <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
              <div className="bg-black rounded-lg overflow-hidden relative w-full max-w-4xl h-96">
                <button
                  onClick={closeVideo}
                  className="absolute top-2 right-2 text-white text-2xl font-bold z-50 bg-red-600 w-8 h-8 rounded-full flex items-center justify-center"
                >
                  &times;
                </button>
                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=1`}
                    title="Client Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />{" "}
                </div>
              </div>
            </div>
          )}

          <div
            className="flex w-full"
            ref={sliderRef}
            onTransitionEnd={handleTransitionEnd}
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
              transition: isTransitioning
                ? "transform 0.5s ease-in-out"
                : "none",
            }}
          >
            {/* Clone last */}
            <div className="w-full flex-shrink-0">
              <div className="relative overflow-hidden rounded-lg group h-64 md:h-96">
                <img
                  src={clientVideos[totalSlides - 1].thumbnail}
                  alt="Video thumbnail"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button className="bg-red-600 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-red-700 transition-colors">
                    <FaPlay className="ml-1" size={20} />
                  </button>
                </div>
              </div>
            </div>

            {/* Real slides */}
            {clientVideos.map((video, index) => (
              <div key={index} className="w-full flex-shrink-0">
                <div className="relative overflow-hidden rounded-lg group h-64 md:h-96">
                  <img
                    src={video.thumbnail}
                    alt={`Video thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <button
                      onClick={() => playVideo(video.id)}
                      className="bg-red-600 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-red-700 transition-colors"
                    >
                      <FaPlay className="ml-1" size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Clone first */}
            <div className="w-full flex-shrink-0">
              <div className="relative overflow-hidden rounded-lg group h-64 md:h-96">
                <img
                  src={clientVideos[0].thumbnail}
                  alt="Video thumbnail"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button className="bg-red-600 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-red-700 transition-colors">
                    <FaPlay className="ml-1" size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center mt-6 mb-12 space-x-2">
          {clientVideos.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index + 1)}
              className={`w-3 h-3 rounded-full transition-colors ${
                getActualIndex() === index ? "bg-yellow-400" : "bg-gray-400"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClientWork;
